import clsx from 'clsx';
import moment from 'moment';
import { Badge, Button, CheckBox, Eyebrow, Icon, Image, Label, Link, Text, Types } from '@marriott/mi-ui-library';
import {
  CREDIT_CARD_CODES,
  PROFILE_CONSTANTS,
  getCountryAndLangCode,
  accountConstants,
  getMaskedDot,
  isCardExpired,
  CLICK_TRACK_DYNAMIC_VALUE_PROFILE_EDIT_BTN,
} from '../../../modules';
import { DOT_SYMBOL } from '../../../modules/utils/constants/constants';

import {
  ActionObject,
  ActionObjectDataType,
  AddressType,
  TitleObject,
  CreditCardDetails,
  FeaturedDynamicImage,
  PaymentMethod,
  ProfileSectionRow,
} from '../ProfileSectionElement/ProfileSectionElement.types';
import { GiftCardsList } from '../../../molecules/GiftCardsList';
import { ImageDetails, ProfileSectionType, Section, SectionList } from '../ProfileSection.types';
import { CustomerPaymentMethod, UserProfile, GiftCardDetails as GiftCard } from '../../../modules/store/profileStore';

/**
 * Obfuscates a gift card serial number with the pattern: •••••••••••X-XXXX-XXX•
 * Uses Unicode LTR embedding to ensure correct display order in RTL locales (ar-AE, he-IL)
 * @param serialNumber - The gift card serial number to obfuscate
 * @returns The obfuscated serial number with the pattern: •••••••••••X-XXXX-XXX•
 */
export const getObfuscatedGiftCardNumber = (serialNumber: string | undefined) => {
  if (!serialNumber) return '';
  if (serialNumber.length < 8) return serialNumber;

  // Take last 8 characters and format as X-XXXX-XXX
  const lastEight = serialNumber.slice(-8);
  const firstDigit = lastEight[0];
  const middleFour = lastEight.slice(1, 5);
  const lastThree = lastEight.slice(5, 8);
  const elevenDots = DOT_SYMBOL.repeat(11);

  const formattedNumber = `${elevenDots}${firstDigit}-${middleFour}-${lastThree}${DOT_SYMBOL}`;

  // Wrap with Unicode LTR embedding characters to ensure correct display in RTL locales
  // \u202A = Left-to-Right Embedding, \u202C = Pop Directional Formatting
  return `\u202A${formattedNumber}\u202C`;
};

const {
  MEMBER_NUMBER,
  LINKED_ACCOUNTS,
  CREDITCARD,
  REMEMBER_ME,
  GIFT_CARDS,
  ADD_GIFT_CARD,
  VIEW_GIFT_CARDS,
  TRUSTED_DEVICES,
} = PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID;

const handleLinkObject = (linkObject: ActionObject) =>
  linkObject?.ctaLink &&
  linkObject?.ctaLabel && (
    <Link
      text={linkObject?.ctaLabel}
      linkClassName="t-font-s text-nowrap border-line"
      linkHref={linkObject?.ctaLink || ''}
      target={linkObject?.openInANewTab ? '_blank' : '_self'}
    />
  );

const handleModalObject = (modalObject: ActionObject, handleModalClick: (link: string) => void) => {
  const clickTrackValue = findClickTrackValue(modalObject?.profileId);
  return (
    modalObject?.ctaLabel && (
      <Button
        buttonCopy={modalObject?.ctaLabel}
        className="t-font-s btn-editLink px-0 border-line align-self-start text-nowrap"
        linkType="internal"
        callback={() => handleModalClick(modalObject?.profileId || '')}
        ariaLabel="model"
        {...(clickTrackValue && { custom_click_track_value: clickTrackValue })}
      />
    )
  );
};
const handleInfoTextObject = (showCTAInfoText: boolean, actionObject: ActionObject | undefined) => {
  if (showCTAInfoText && actionObject?.hiddenCTAInfoText) {
    return (
      <Text
        customClass={actionObject.hiddenCTAInfoText}
        copyText={actionObject.hiddenCTAInfoText}
        fontSize={Types.size.small}
        element={Types.tags.span}
      />
    );
  }
  return null;
};

const findClickTrackValue = (profileId: string) => {
  return CLICK_TRACK_DYNAMIC_VALUE_PROFILE_EDIT_BTN[profileId];
};
const handleModalLinkObject = (
  modalLinkObject: ActionObject,
  handleModalClick: (link: string, isVistanaModal: boolean) => void
) =>
  modalLinkObject?.ctaLabel && (
    <Button
      buttonCopy={modalLinkObject?.ctaLabel}
      className="t-font-s btn-editLink px-0 border-line text-nowrap"
      linkType="internal"
      callback={() => handleModalClick(modalLinkObject?.modalLink || '', true)}
      ariaLabel="model"
      testId={modalLinkObject.profileId}
    />
  );

const handleImageObject = (imgObject: ImageDetails[], linkedAccount?: string[]) =>
  imgObject && (
    <div className="image-wrapper d-flex">
      {imgObject.map((img, index) => {
        const { linkedAccountId } = img;
        const showLinkedAccount = linkedAccountId && linkedAccount?.includes(linkedAccountId);
        return showLinkedAccount && handleImageObjectUXL(img, index);
      })}
    </div>
  );

const handleImageObjectUXL = (imgObject: ImageDetails, index: number) => {
  const { imageRedirectLink, imageAltText, featuredDynamicImage, openImageRedirectLinkInANewTab } = imgObject;
  return imageRedirectLink ? (
    <Link
      linkClassName="ml-3"
      linkHref={imageRedirectLink}
      key={index}
      target={openImageRedirectLinkInANewTab ? '_blank' : '_self'}
      children={
        <Image
          title={imageAltText}
          defaultImageURL={featuredDynamicImage?.assetPath}
          altText={imageAltText}
          customClass="img-fluid"
          loading="lazy"
        />
      }
    />
  ) : (
    <Image
      title={imageAltText}
      defaultImageURL={featuredDynamicImage?.assetPath}
      altText={imageAltText}
      customClass="img-fluid"
      loading="lazy"
    />
  );
};

const handleCheckboxObject = (
  checkboxObject: ActionObject,
  isChecked: boolean,
  handleToggleCheckbox: () => void,
  isRememberLoading?: boolean
) =>
  checkboxObject && (
    <CheckBox
      checkboxId="remember-me"
      checkboxName="remember-me"
      className="mb-0"
      checkboxLabel=""
      checked={isChecked}
      onChange={handleToggleCheckbox}
      data-testId="remember-me"
      disabled={isRememberLoading}
    />
  );

export const handleTitleObject = (
  titleObject: TitleObject | undefined,
  profileId: string,
  isTabletAndAboveViewPort?: boolean
) => {
  const isCreditcard = profileId === CREDITCARD;
  const isGiftCard = profileId === GIFT_CARDS;

  // For credit cards and gift cards, always render titleElement if it exists
  if ((isCreditcard || isGiftCard) && titleObject?.titleElement) {
    return titleObject?.titleElement;
  }

  // For other items, render text or tooltip
  if (titleObject && titleObject?.text && !isCreditcard) {
    return titleObject?.toolTipText ? (
      <div className="d-flex">
        <div className="popover-container">
          <div className="d-flex popover-wrapper">
            <Text element={Types.tags.div} fontSize={Types.size.medium} copyText={titleObject?.text} />
            <Icon iconClass="icon-alert px-1" />
          </div>
          <div
            className={clsx(
              'd-flex popover-card inverse ',
              !isTabletAndAboveViewPort ? 'm-popover-bottom' : 'm-popover-right'
            )}
            data-testid="tooltip-card"
            data-content="Inverse Theme"
          >
            <Text fontSize={Types.size.small} element={Types.tags.div} copyText={titleObject?.toolTipText} />
          </div>
        </div>
      </div>
    ) : (
      <Text element={Types.tags.div} fontSize={Types.size.medium} copyText={titleObject?.text} />
    );
  } else {
    return titleObject?.titleElement;
  }
};

// This is RHS item for each line
export const handleActionObject = (
  actionObject: ActionObject | undefined,
  handleModalClick: (link: string, isVistanaModal?: boolean) => void,
  isChecked: boolean = false,
  handleToggleCheckbox: () => void,
  memberNumber: string,
  dynamic: boolean | undefined,
  linkedAccount?: string[],
  isRememberLoading: boolean = false,
  showCTAInfoText: boolean = false
) => {
  if (!dynamic) {
    // Handle dynamic cases with hiddenCTAInfoText
    if (showCTAInfoText) {
      return handleInfoTextObject(showCTAInfoText, actionObject);
    }
    if (actionObject?.isGiftCardLimitReached && actionObject?.ctaLabel) {
      return (
        <Text
          customClass="t-font-s"
          copyText={actionObject.ctaLabel}
          fontSize={Types.size.small}
          element={Types.tags.span}
        />
      );
    }
    switch (actionObject?.ctaType) {
      case 'link':
        return handleLinkObject(actionObject);
      case 'modal':
        return handleModalObject(actionObject, link => handleModalClick(link));
      case 'linkModal':
        return handleModalLinkObject(actionObject, (link, isVistanaModal = false) =>
          handleModalClick(link, isVistanaModal)
        );
      case 'image':
        return handleImageObject(actionObject?.imageDetails || [], linkedAccount);
      case 'checkbox':
        return handleCheckboxObject(actionObject, isChecked, handleToggleCheckbox, isRememberLoading);
      default:
        return '';
    }
  } else {
    // Handle dynamic cases with line item ID checks
    switch (actionObject?.profileId) {
      case MEMBER_NUMBER:
        return (
          <Text
            customClass={clsx(MEMBER_NUMBER)}
            element={Types.tags.div}
            fontSize={Types.size.small}
            copyText={memberNumber}
          />
        );
      case LINKED_ACCOUNTS:
        return handleImageObject(actionObject?.imageDetails || [], linkedAccount);
      case CREDITCARD:
        return handleModalObject(actionObject, link => handleModalClick(link));
      case VIEW_GIFT_CARDS:
        return handleModalObject(actionObject, link => handleModalClick(link));
      case ADD_GIFT_CARD:
        return handleModalObject(actionObject, link => handleModalClick(link));
      case REMEMBER_ME:
        return handleCheckboxObject(actionObject, isChecked, handleToggleCheckbox, isRememberLoading);
      default:
        return '';
    }
  }
};

export const sectionTitleHelper = (sectionTitle: string, className?: string, isVariationV2?: boolean) => {
  if (!sectionTitle) return null;
  return isVariationV2 ? (
    <div className={clsx('p-3 p-md-4 section-heading', className)}>
      <Text
        fontSize={Types.size.extraLarge}
        element={Types.tags.div}
        copyText={sectionTitle}
        customClass="t-subtitle-xl section-title"
      />
    </div>
  ) : (
    <div className={` mb-3 color-scheme1 section-title text_modal-align ${className}`}>
      <Eyebrow text={sectionTitle} />
    </div>
  );
};

export const getCreditCardDetail = (
  userPayment: CustomerPaymentMethod[],
  cardImage: FeaturedDynamicImage | undefined,
  isTabletAndAboveViewPort?: boolean
) => {
  if (!userPayment || userPayment?.length === 0) {
    return [];
  }

  const processCard = (card: CustomerPaymentMethod) => {
    const cardCode = card?.issuerType?.code || card?.cobrand?.issuer?.code;
    const isExpiredCard = isCardExpired(card?.expiration);
    const cardDetail = {
      icon: cardImage, // Use empty string if icon is undefined
      label: cardCode || '', // Use issuerType label if lookup label is undefined
      maskedDot: getMaskedDot(!isTabletAndAboveViewPort), // passed true for single dot variation in mobile
      pin: card?.panLast4,
      isPreferred: card?.preferred || false,
      isExpired: isExpiredCard,
      nickname: card?.nickname || '',
      createtedtimestamp: card?.createTimestamp,
      cobrandissuercode: card?.cobrand?.issuer?.code,
    };

    return cardDetail;
  };

  const processedCardDetails = [];

  // Check if there are payment methods
  if (userPayment.length > 0) {
    // Check if there is a preferred card
    const preferredCard = userPayment?.find((card: CustomerPaymentMethod) => card?.preferred);

    if (preferredCard) {
      // Process the preferred card first
      const preferredCardDetail = processCard(preferredCard);
      processedCardDetails.push(preferredCardDetail);
    } else {
      // If no preferred card, process the first 10 non-preferred cards or all available if less than 10
      const nonPreferredCards = userPayment.filter((card: CustomerPaymentMethod) => !card?.preferred).slice(0, 9);
      nonPreferredCards.forEach((card: CustomerPaymentMethod) => {
        const nonPreferredCardDetail = processCard(card);
        processedCardDetails.push(nonPreferredCardDetail);
      });
    }
  }
  return processedCardDetails;
};

export const getCardDetailStructure = (
  cardDetails: CreditCardDetails[],
  defaultLabel?: string,
  expiryLabel?: string,
  cardTag?: string,
  cardDescription?: string,
  isChinaLocale?: boolean
) => {
  const cardStructurehtml = cardDetails.map((cardDetail: CreditCardDetails) => {
    return (
      <div>
        <div className="d-flex flex-column flex-md-row align-items-md-center">
          <div>
            <div className="d-flex align-items-center">
              <Image
                customClass={clsx(`cardData__${CREDIT_CARD_CODES[cardDetail.label] || ''}`)}
                altText={'credit-card-' + cardDetail.label}
                defaultImageURL={
                  ((isChinaLocale ? cardDetail?.icon?.damPath : cardDetail?.icon?.assetPath) as string) || ''
                }
                loading="eager"
              />
              <div className="cardData__cardpin min-width-max-content pl-4 ml-5 d-flex t-font-s">
                {cardDetail.maskedDot} {cardDetail.pin}
              </div>
            </div>
            <div>
              <Label
                customClass="cardData__nickname pl-5 ml-4"
                labelText={cardDetail.nickname}
                size={Types.size.small}
              />
            </div>
          </div>
          <div className="ml-5 pl-4 pl-md-0 ml-md-4 mt-2 mt-md-0 d-flex align-items-center">
            {cardDetail.isPreferred && (
              <Label
                labelText={defaultLabel ?? ''}
                customClass="cardData__defaultLabel mr-2 mr-lg-0"
                size={Types.size.small}
              />
            )}
            {cardDetail.isExpired && (
              <Label
                customClass={clsx(
                  'cardData__expiryLabel',
                  cardDetail.isPreferred && 'mt-2 mt-lg-0 ml-0 ml-lg-3 ml-lg-3 ml-0'
                )}
                size={Types.size.small}
                labelText={expiryLabel ?? ''}
              />
            )}
            {cardDetail?.cobrandissuercode && moment().diff(moment(cardDetail?.createtedtimestamp), 'days') < 14 && (
              <Badge
                badgeVariation={Types.BadgeVariation?.Overlay}
                badgeText={cardTag || ''}
                isInlineMerch={true}
                customClass={clsx('p-1 d-lg-none d-block min-width-max-content', cardDetail.isPreferred)}
              />
            )}
          </div>
          {cardDetail?.cobrandissuercode && (
            <>
              {moment().diff(moment(cardDetail?.createtedtimestamp), 'days') < 14 && (
                <div className="ml-5 pl-4 pl-md-0 ml-md-4 d-lg-block d-none">
                  <Badge
                    badgeVariation={Types.BadgeVariation?.Overlay}
                    badgeText={cardTag || ''}
                    isInlineMerch={true}
                    customClass="p-1 min-width-max-content"
                  />
                </div>
              )}
              <div className="ml-5 pl-4 pl-md-0 ml-md-4 mt-3 mt-md-0">
                <div className="d-flex ">
                  <Icon iconClass="icon-more-points-on-stays icon-s" />
                  <Text
                    element={Types.tags.span}
                    fontSize={Types.size.extraSmall}
                    copyText={cardDescription || ''}
                    customClass="ml-2 t-font-xs align-self-center"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  });
  return cardStructurehtml;
};
export const renderLineItemHelper = (
  actionObjectData: ActionObjectDataType | null,
  rowIndex: number,
  sectionLength: number,
  profileDetails: UserProfile,
  isTabletAndAboveViewPort?: boolean,
  showCTAInfoText?: boolean
) => {
  if (actionObjectData === null) {
    return '';
  }
  const profileId = actionObjectData?.actionObject?.profileId || '';
  const {
    titleObject,
    actionObject,
    handleModalClick,
    isRememberMe,
    handleToggleCheckbox,
    memberNumber,
    isRememberLoading,
  } = actionObjectData;
  const sectionBorderClass = rowIndex + 1 < sectionLength ? 'section-border' : '';
  const isCreditCardAvailable = profileDetails?.paymentMethods?.length > 0;
  const linkedAccount = profileDetails?.linkedAccount;
  const paddingClass =
    rowIndex === 0 && rowIndex === sectionLength - 1
      ? '' // This is when we have only one line item
      : profileId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID.ADD_CARD && !isCreditCardAvailable
      ? 'pb-4' // This is for first line item of  ADDCARD if no credit card is added
      : profileId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID.CREDITCARD && rowIndex === -1
      ? 'pb-4' // This is for first line item of CREDITCARD
      : profileId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID.CREDITCARD && rowIndex >= 0
      ? 'pt-4 pb-4' // This is for non-first line items of CREDITCARD
      : rowIndex === 0 && profileId !== PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID.CREDITCARD
      ? 'pb-4' // This is for first line item
      : rowIndex === sectionLength - 1
      ? 'pt-4' // This is for last line item
      : profileId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID.LINKED_ACCOUNTS
      ? 'pb-3 pt-3'
      : 'pb-4 pt-4'; // This is for non-first and non-last line items

  return (
    <div className={clsx(sectionBorderClass, paddingClass, 'row mx-0')}>
      <div className="col-12 d-flex justify-content-between align-items-center px-0 item-gap" data-testid="line-item">
        {titleObject && handleTitleObject(titleObject, profileId, isTabletAndAboveViewPort)}
        {handleActionObject(
          //image, link, modal object
          actionObject,
          handleModalClick,
          isRememberMe,
          handleToggleCheckbox,
          memberNumber,
          actionObject?.dynamic,
          linkedAccount,
          isRememberLoading,
          showCTAInfoText
        )}
      </div>
    </div>
  );
};

export const profileSectionsHelper = (profilesections: Section) => {
  const profileObject: ProfileSectionType = {
    sectionTitle: profilesections?.sectionTitle,
    showForUsers: profilesections?.showForUsers,
    profileSectionRow: profilesections?.sectionList?.map((ele: SectionList) => {
      return {
        titleObject: {
          text: ele?.profileTitle,
          titleElement: '', // TO DO going replace with dynamic html based on UXL data,
          defaultLabel: profilesections?.defaultLabel,
          expiryLabel: profilesections?.expiredLabel,
          cardTag: profilesections?.cardTag,
          cardDescription: profilesections?.cardDescription,
          featuredDynamicImage: profilesections?.featuredDynamicImage,
          toolTipText: ele?.toolTipText,
          activeLabel: profilesections?.activeLabel,
          onHoldLabel: profilesections?.onHoldLabel,
          expiringSoonLabel: profilesections?.expiringSoonLabel,
          lockedLabel: profilesections?.lockedLabel,
          maxGiftCards: profilesections?.maxGiftCards,
          maxGiftCardsReachedLabel: profilesections?.maxGiftCardsReachedLabel,
          image: profilesections?.image,
          imageAltText: profilesections?.imageAltText,
          expiresLabel: profilesections?.expiresLabel,
          balanceLabel: profilesections?.balanceLabel,
        },
        actionObject: {
          imageDetails: ele?.imageDetails,
          openInANewTab: ele?.openInANewTab,
          ctaLink: ele?.ctaLink,
          modalLink: ele?.modalLink,
          ctaType: ele?.ctaType,
          profileId: ele?.profileId,
          conditionalForLineItem: ele?.conditionalForLineItem,
          conditionType: ele?.conditionType,
          allowedCountries: ele?.allowedCountries,
          allowedUsers: ele?.allowedUsers,
          dynamic: ele?.dynamic,
          ctaLabel: ele?.ctaLabel,
          hiddenCTAInfoText: ele?.hiddenCTAInfoText,
          openImageRedirectLinkInANewTab: ele?.openImageRedirectLinkInANewTab,
        },
      };
    }),
  };
  return profileObject;
};

export const countryCodeHelper = (profileAddress: AddressType[], profileId: string, currentLocale: string) => {
  const userProfileCountryCode = profileAddress?.filter(address => address?.primary === true)?.[0];
  const userCountryCode = userProfileCountryCode?.address?.country?.code;
  return PROFILE_CONSTANTS.CONDITIONAL_USER_COUNTRY_LINE_IDS.includes(profileId)
    ? userCountryCode
    : getCountryAndLangCode(currentLocale).countryCode || accountConstants?.DEFAULT_COUNTRY;
};

export const checkPaymentMethods = (paymentMethods: PaymentMethod[] = []) => {
  const currentTime = moment();

  const isTimestampValid = (method: PaymentMethod) =>
    currentTime.isBefore(moment(method?.createTimestamp).add(20, 'minutes'));

  const isValid = (method: PaymentMethod) => method?.cobrand?.issuer?.code && isTimestampValid(method);

  const isPending = (method: PaymentMethod) => {
    const methodTime = moment(method?.createTimestamp);

    // Pending cases:
    // 1. No cobrand issuer code and no timestamp
    // 2. Has cobrand issuer code and timestamp, but date is NOT current date
    // 3. Has cobrand issuer code and timestamp on same day, within 20 minutes (still processing)
    return (
      (!method?.cobrand?.issuer?.code && !method?.createTimestamp) || // 1st condition
      (method?.cobrand?.issuer?.code && method?.createTimestamp && !methodTime.isSame(currentTime, 'day')) // 2nd condition
    );
  };

  const validMethods = paymentMethods?.filter(isValid);
  const hasPendingMethods = paymentMethods == null || paymentMethods.some(isPending);

  return validMethods?.length > 0 ? 'success' : hasPendingMethods ? 'pending' : 'failure';
};

// Gift Card Helper Functions

export interface GiftCardDetails {
  icon: FeaturedDynamicImage | undefined;
  serialNumber: string;
  balance: number;
  currency: string;
  expirationDate: string | null;
  isExpired: boolean;
  cardStatus: string;
  cardImageUrl: string | null;
  cardISO: string;
  hasHoldNotLost: boolean | null;
  isAutoReload: boolean;
  pointsBalance: number;
  createTimestamp?: string;
}

export const getAddGiftCardButtonData = (
  actionObjectData: ActionObjectDataType,
  profileRow: ProfileSectionRow,
  filteredGiftCards: GiftCard[]
) => {
  const maxGiftCards = profileRow?.titleObject?.maxGiftCards
    ? Number(profileRow?.titleObject?.maxGiftCards)
    : undefined;
  // Use filteredGiftCards.length (excludes Stored Value cards) instead of total giftCardsCount
  // This ensures users can add up to maxGiftCards regular gift cards regardless of Stored Value cards
  const isAtMaxCards =
    maxGiftCards !== undefined && !isNaN(maxGiftCards) ? filteredGiftCards.length >= maxGiftCards : false;

  // Create modified action object with updated button text
  const modifiedActionObjectData = {
    ...actionObjectData,
    actionObject: {
      ...actionObjectData.actionObject,
      imageDetails: actionObjectData.actionObject?.imageDetails || null,
      profileId: actionObjectData.actionObject?.profileId || '',
      conditionalForLineItem: actionObjectData.actionObject?.conditionalForLineItem || false,
      conditionType: actionObjectData.actionObject?.conditionType || '',
      allowedCountries: actionObjectData.actionObject?.allowedCountries || [],
      allowedUsers: actionObjectData.actionObject?.allowedUsers || [],
      dynamic: actionObjectData.actionObject?.dynamic || false,
      ctaLabel: isAtMaxCards
        ? profileRow?.titleObject?.maxGiftCardsReachedLabel
        : actionObjectData.actionObject?.ctaLabel,
      isGiftCardLimitReached: isAtMaxCards,
    },
  };

  // Calculate the correct row index for the Add button
  // If there are gift cards, this should be the last item in the section
  // If no gift cards, this should be the first (and only) item
  const addButtonRowIndex = filteredGiftCards.length > 0 ? filteredGiftCards.length : 0;

  return {
    modifiedActionObjectData,
    addButtonRowIndex,
  };
};

export const giftCardObjectDataHelper = (
  userGiftCardDetails: GiftCard[],
  giftCardLabel: ProfileSectionRow,
  _profileSection: ProfileSectionType,
  handleViewGiftCardModal: (giftCard: GiftCard) => void,
  _isTabletAndAboveViewPort?: boolean
) => {
  if (userGiftCardDetails.length > 0) {
    // Create proper action object like credit cards
    const giftCardActionObject = {
      ...giftCardLabel.actionObject,
      ctaType: 'modal',
      ctaLabel: giftCardLabel.actionObject.ctaLabel, // Use AEM ctaLabel
      profileId: GIFT_CARDS,
    };

    return {
      titleObject: {
        text: `text-${giftCardLabel.actionObject.profileId}`, // Match credit card pattern
        titleElement: (
          <GiftCardsList
            giftCards={userGiftCardDetails}
            featuredDynamicImage={giftCardLabel?.titleObject?.featuredDynamicImage}
            expiresLabel={giftCardLabel?.titleObject?.expiresLabel}
            balanceLabel={giftCardLabel?.titleObject?.balanceLabel}
            expiringSoonLabel={giftCardLabel?.titleObject?.expiringSoonLabel}
            expiredLabel={giftCardLabel?.titleObject?.expiryLabel}
            lockedLabel={giftCardLabel?.titleObject?.lockedLabel}
            newCardLabel={giftCardLabel?.titleObject?.cardTag}
            activeLabel={giftCardLabel?.titleObject?.activeLabel}
            onHoldLabel={giftCardLabel?.titleObject?.onHoldLabel}
            image={giftCardLabel?.titleObject?.image}
            imageAltText={giftCardLabel?.titleObject?.imageAltText}
          />
        ),
      },
      actionObject: giftCardActionObject,
      handleModalClick: () => {
        handleViewGiftCardModal(userGiftCardDetails[0]);
      },
      isRememberMe: false,
      memberNumber: '',
      handleToggleCheckbox: () => {},
      isRememberLoading: false,
    };
  }

  return null;
};

export const getMaskedGiftCardNumber = (serialNumber: string | undefined) => {
  if (!serialNumber) return '';
  if (serialNumber.length < 8) return serialNumber;

  // Take last 4 characters and format as •••• •••• ••••• XXXX
  const lastFour = serialNumber.slice(-4);
  const dotsGroup = `${DOT_SYMBOL.repeat(4)} ${DOT_SYMBOL.repeat(4)} ${DOT_SYMBOL.repeat(5)}`;

  const formattedNumber = `${dotsGroup} ${lastFour}`;

  // Wrap with Unicode LTR embedding characters to ensure correct display in RTL locales (ar-AE, he-IL)
  // \u202A = Left-to-Right Embedding, \u202C = Pop Directional Formatting
  return `\u202A${formattedNumber}\u202C`;
};

/**
 * Gift card number display format aligned with iOS: first 4 digits, two dots, one digit, one dot.
 * Pattern: XXXX••X• (e.g. 3086••3•)
 * Used consistently across Web, Android, and iOS for gift card list display.
 * @param serialNumber - The gift card serial number
 * @returns Formatted display string with Unicode LTR embedding for RTL locales
 */
export const getGiftCardNumberDisplayFormat = (giftCardOrISONumber?: string, giftCardSerialNumber?: string) => {
  if (!giftCardOrISONumber && !giftCardSerialNumber) return '';

  let isoNumberFourDigits = '';
  let serialNumberLastDigit = '';

  if (!giftCardSerialNumber && giftCardOrISONumber && giftCardOrISONumber.length > 6) {
    isoNumberFourDigits = giftCardOrISONumber.slice(0, 4);
    serialNumberLastDigit = giftCardOrISONumber[giftCardOrISONumber.length - 2];
  } else {
    isoNumberFourDigits = giftCardOrISONumber?.slice(0, 4) || '';
    serialNumberLastDigit = giftCardSerialNumber?.[giftCardSerialNumber.length - 1] || '';
  }

  const formattedNumber = `${isoNumberFourDigits}${DOT_SYMBOL}${DOT_SYMBOL}${serialNumberLastDigit}${DOT_SYMBOL}`;

  // Wrap with Unicode LTR embedding for RTL locales (ar-AE, he-IL)
  return `\u202A${formattedNumber}\u202C`;
};
